import { Injectable } from '@angular/core';
import { GlobalsPaths } from 'app/app.config';
import { IInvestigationFormType, IPaginatedVehicleActivityRow, IVehicleActivityApiParams, IVehicleActivityRow, IVehicleFileForm } from 'app/_models/vehicle-file';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '../_services/global-http.service';
import { AuthenticationService } from './authentication.service';
import { environment } from 'environments/environment';

@Injectable()
export class VehiclesFilesService {

    currentUser: any = JSON.parse(localStorage.getItem('currentUser'));

    data: any;

    constructor(
        private http: HttpClient,
        private globalsPaths: GlobalsPaths,
        private authService: AuthenticationService
    ) {
    }

    getVehicleActivityTypes(vehicleTypeFilter?: string, filterOutInternal: boolean = false) {
        this.data = this.http.get(`/api/vehicle_activity_types/?page=1&pageSize=15&ordering=name${vehicleTypeFilter ? `&vehicle_type=${vehicleTypeFilter}` : ''}&remove_internal_use=${filterOutInternal}`);
        return this.data;
    }

    // TODO
    getScheduleActivities() {
        this.data = this.http.get(`/api/schedule_activity/?page=1&pageSize=50`);
        return this.data;
    }


    getScheduleTypes(params?) {
        let _qs = 'ordering=name';
        if (params) {
            Object.keys(params).forEach((key) => (params[key] == null) && delete params[key]);
            _qs = Object.keys(params).map(key => key + '=' + params[key]).join('&');
        }

        this.data = this.http.get(`/api/schedule_types/?${_qs}`);
        return this.data;
    }


    getScheduleType(id: number) {
        this.data = this.http.get(`/api/schedule_types/${id}/`);
        return this.data;
    }

    getSingleVehicleActivityTypes(vehicleId) {
        this.data = this.http.get('/api/vehicle_activity_types/' + vehicleId + '/');
        return this.data;
    }

    getVehicleActivities(params: IVehicleActivityApiParams): Observable<IPaginatedVehicleActivityRow> {
        Object.keys(params).forEach((key) => (params[key] == null) && delete params[key]);
        let _qs = Object.keys(params).map(key => key + '=' + params[key]).join('&');
        return this.http.get<IPaginatedVehicleActivityRow>(`/api/vehicle_activities/?${_qs}`);
    }

    getVehicleActivityInvestigations(activityId: number): Observable<any> {
        return this.http.get<any>(`/api/vehicle_activities/${activityId}/investigations/`);
    }

    getInvestigationDetails(activityId: number, investigationId: number): Observable<any> {
        return this.http.get<any>(`/api/vehicle_activities/${activityId}/investigations/${investigationId}/`);
    }

    updateInvestigation(activityId: number, investigationId: number, data: any): Observable<any> {
        return this.http.patch(`/api/vehicle_activities/${activityId}/investigations/${investigationId}/`, data);
    }

    getInvestigationFormTypes(activityId: number, investigationId: number): Observable<IInvestigationFormType[]> {
        return this.http.get<IInvestigationFormType[]>(
            `${this.investigationUrl(activityId, investigationId)}form_types/`);
    }

    /***
     * Chooses the form type, which builds the blank form from its questions.
     * Only valid while the investigation is awaiting one, and cannot be undone.
     * Responds with the full investigation detail, so no refetch is needed.
     */
    selectInvestigationFormType(activityId: number, investigationId: number, formTypeId: number): Observable<any> {
        return this.http.post(
            `${this.investigationUrl(activityId, investigationId)}select_form_type/`,
            { form_type: formTypeId });
    }

    /***
     * Signs off a filled in form. Moves it to approval where the form type
     * requires it, and completes it outright where it does not.
     */
    signOffInvestigation(activityId: number, investigationId: number, signature?: string): Observable<any> {
        const _payload = signature ? { signoff_signature: signature } : {};
        return this.http.post(
            `${this.investigationUrl(activityId, investigationId)}sign_off/`, _payload);
    }

    /***
     * Records an approver's decision. A comment is required when rejecting.
     */
    reviewInvestigation(activityId: number, investigationId: number, decision: 'APPROVED' | 'REJECTED',
                        comment?: string, signature?: string): Observable<any> {
        const _payload: any = { decision: decision };
        if (comment) {
            _payload.comment = comment;
        }
        if (signature) {
            _payload.signature = signature;
        }
        return this.http.post(
            `${this.investigationUrl(activityId, investigationId)}review/`, _payload);
    }

    /***
     * The PDF needs the auth header, so it is fetched as a blob rather than
     * linked to directly. `isJson` is off so the raw response comes back.
     */
    getInvestigationPdf(activityId: number, investigationId: number): Observable<any> {
        return this.http.get(
            `${this.investigationUrl(activityId, investigationId)}pdf/`,
            { responseType: 3 }, false, null, false);
    }

    private investigationUrl(activityId: number, investigationId: number): string {
        return `/api/vehicle_activities/${activityId}/investigations/${investigationId}/`;
    }

    postVehicleActivities(data: IVehicleFileForm, scheduleThisActivity: boolean = false, fufillThisActivity: boolean = true): Observable<IVehicleFileForm> {
        // If there is a schedule type assigned to activity we need the below logic
        const _activityId = data.activity_type['id'];
        if (scheduleThisActivity) {
            data.activity_type = null;
        }
        const _formData = this.createFormData(data);
        return this.http.post(`/api/vehicle_activities/?overwrite_task_queue=True${scheduleThisActivity ?
            `${fufillThisActivity ? '&fulfill_scheduled_activity=True' : ''}&filter_schedule_by_vehicle_type=True&scheduled_activity_type=` + _activityId : ''}`, _formData);
    }

    /***
     * NOTE: this is only user for a manual upload of a vehicle Safety Inspection.
     This will schedule ScheduleActivities for the selected vehicle
     */
    postManualSafetyInspection(data: IVehicleFileForm, scheduleThisActivity: boolean = false, vehicleReg: string): Observable<IVehicleFileForm> {
        // If there is a schedule type assigned to activity we need the below logic
        const _activityId = data.activity_type['id'];
        if (scheduleThisActivity) {
            data.activity_type = null;
        }
        data.description = 'Manual Safety Inspection for ' + vehicleReg;
        data['status'] = 7; // Set to 'Complete'
        const _formData = this.createFormData(data);
        // tslint:disable-next-line:max-line-length
        return this.http.post(`/api/vehicle_activities/?fulfill_scheduled_activity=True&filter_schedule_by_vehicle_type=True&scheduled_activity_type_reference=SAFETY_INSPECTION&overwrite_task_queue=True`, _formData);
    }

    getVehicleGroupDepots() {

        this.data = this.http.get('/api/fmt/vehicle_groups/v2/?page=1&pageSize=15');
        return this.data;
    }

    postDefectReportPdf(id: number, jwt: string) {
        // TODO Get the JTW token (temp fix until Defect and SI are on SSO - bearer tokens)
        return this.http.post(`${this.globalsPaths.defectApiHost}/defect/${id}/generate_pdf/`, null, { 'Authorization': 'JWT ' + jwt }, true, false);
    }

    getSafetyInspectionReportPdf(id: number, token: string) {
        const extAppKey: string = environment.externalAppKeys.dpd;
        // TODO Get the JTW token (temp fix until Defect and SI are on SSO - bearer tokens)


        if (!token) {
            return this.http.get(`${this.globalsPaths.safetyInspectionApiHost}/inspection/${id}/pdf/`,
                { responseType: 3 }, true, { 'Authorization': extAppKey });
        } else {
            return this.http.get(`${this.globalsPaths.safetyInspectionApiHost}/inspection/${id}/pdf/?authorization=JWT ${token}`,
                { responseType: 3 }, true);
        }

    }


    /**
     * For getting creating FormData
     */
    private createFormData(data = {}) {
        const form = new FormData();

        for (let i = 0; i < Object.entries(data).length; i++) {
            const param: any = Object.entries(data)[i];
            if (param[1]) {
                form.append(param[0], param[1]);
            }
        }

        return form;
    }

}




// WEBPACK FOOTER //
// /Users/brianyoung/Documents/Development/hfg/datalive-core/gae-web/datalive/client/src/app/_services/vehicles-files.service.ts